// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import loadImage from "blueimp-load-image";

import Properties from "../../Properties";
import type {
  JsonOkResponseType,
  Json400ResponseType,
} from "../../global/types/JsonResponseType.js";
import type { UploadStatesType, UploadPropsType } from "../types/UserFormType";
import { postInit, getInit } from "../../global/types/constants";
import UploadField from "./UploadField";
import {
  detectImage,
  saveToken,
  saveResults,
} from "../../global/actions/databaseActions";
import WebCamera from "./WebCamera";
import LoadingProgress from "../../global/components/LoadingProgress";
import { ColorSchema } from "../../global/types/scss";
import { buttonStyles } from "../../global/components/StyledButtons";
import { getCookie } from "../../global/functions/cookies";
import getMobileOperatingSystem from "../../global/functions/getMobileOs";

import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Hidden from "@material-ui/core/Hidden";
import { withStyles } from "@material-ui/core/styles";

class Upload extends Component<UploadPropsType, UploadStatesType> {
  switch2Camera: Function;
  resetForm: Function;
  setImg: Function;
  captureCamera: Function;
  constructor(props: UploadPropsType) {
    super(props);
    this.state = {
      loading: false,
      isCamera: false,
    };
    this.switch2Camera = this.switch2Camera.bind(this);
    this.resetForm = this.resetForm.bind(this);
    this.setImg = this.setImg.bind(this);
    this.captureCamera = this.captureCamera.bind(this);
  }

  uploadFile = async (file: File | Blob): * => {
    let url = "detect";
    const isSearch = this.props.isSearch;
    const postFormData = new FormData();
    postFormData.append("image", file);
    if (isSearch) {
      postFormData.append("min_match_quality", 0.1);
      url = "search";
    }
    try {
      const accessToken = getCookie("access_token");
      const res: Response = await fetch(
        `${Properties.api_root}/api/v2/conferences/${this.props.db.conference.id}/people/${accessToken}/${url}`,
        postInit(postFormData)
      );
      if (res.ok) {
        const jsonResponse: JsonOkResponseType = await res.json();
        this.props.actions.saveToken(jsonResponse.images[0].image_token);
        if (isSearch) {
          this.props.actions.saveResults(jsonResponse.images[0].results);
        } else {
          this.props.actions.detectImage(jsonResponse.images[0].faces);
        }

        this.livenessCheck(file);

        const loadingImage = loadImage(file, this.setImg, {
          orientation: true,
          canvas: true,
        });
        //let img = new Image();
        //img.onload = (() => {
        //  this.setState({ img: img, loading: false });
        //});
        //img.src = URL.createObjectURL(file);
      } else if (res.status === 400) {
        const jsonResponse: Json400ResponseType = await res.json();
        this.uploadFailCall(jsonResponse.errors.image || "Wrong Image");
      } else {
        this.uploadFailCall(res.statusText);
      }
    } catch (error) {
      this.uploadFailCall(error.message);
    }
  };

  livenessCheck = async (file: File | Blob): * => {
    const accessToken = getCookie("access_token");
    const postFormData = new FormData();
    postFormData.append("image", file);
    let platform = "DESKTOP";
    if (getMobileOperatingSystem() === "iOS") {
      platform = "IOS";
    }
    if (getMobileOperatingSystem() === "Android") {
      platform = "ANDROID";
    }
    postFormData.append("platform", platform);
    const res: Response = await fetch(
      `${Properties.api_root}/api/v2/conferences/${this.props.db.conference.id}/people/${accessToken}/checkLiveness`,
      postInit(postFormData)
    );
    if (res.ok) {
      const jsonResponse: JsonOkResponseType = await res.json();
      this.props.setLiveness(jsonResponse);
    } else {
      this.uploadFailCall(res.statusText);
    }
  };

  setImg(img: HTMLCanvasElement) {
    if (this.props.isSearch) {
      this.props.setImg(img);
    } else {
      const imgUrl = img.toDataURL("image/png");
      this.props.setImg(imgUrl);
    }
  }

  uploadFileHandler = (e: Event): * => {
    this.props.setImg(null);
    this.props.setLiveness(null);
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target) {
      file = e.target.files[0];
    }
    if (file) {
      this.uploadFile(file);
    } else {
      this.uploadFailCall(
        "Website images cannot be uploaded, please upload them from your computer."
      );
    }
  };

  uploadFailCall(msg: string) {
    this.props.setImageError(msg);
    this.setState({ loading: false });
  }

  captureCamera = async (data: string): * => {
    this.props.setImg(null);
    this.props.setLiveness(null);
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    fetch(data)
      .then((res: Response) => res.blob())
      .then((blob: Blob) => {
        // Edge not support File Construct
        file = new Blob([blob], { type: "image/png" });
        this.uploadFile(file);
      });
  };

  switch2Camera() {
    this.setState({ isCamera: true });
  }

  resetForm() {
    this.props.setImageError("");
    this.props.setImg(null);
    this.setState({ isCamera: false, loading: false });
  }

  render(): React$Element<*> {
    const classes = this.props.classes;
    if (this.state.loading) {
      return (
        <div className="form-text">
          <LoadingProgress size={100} />
        </div>
      );
    } else if (this.state.isCamera) {
      return (
        <WebCamera
          captureCamera={this.captureCamera}
          cancelCamera={this.resetForm}
          setError={() =>
            this.props.setImageError("Can NOT open your web camera.")
          }
        />
      );
    } else {
      return (
        <div>
          <UploadField uploadFileActions={this.uploadFileHandler} />
          <Typography
            variant="caption"
            className="help-text"
            style={{ color: ColorSchema("primary") }}
          >
            *Supported Image Formats: PNG | JPG | BMP | WEBP
            <br />
            *Image File Size: less than 10 MB
          </Typography>
          {getMobileOperatingSystem() !== "iOS" && (
            <Button
              color="primary"
              variant="raised"
              type="submit"
              onClick={this.switch2Camera}
              className={classes.primaryBtn}
              style={{ width: "100%" }}
            >
              {this.props.isSearch
                ? "Upload Selfie via Web Camera"
                : "Take Photo ID Image by Web Camera"}
            </Button>
          )}
        </div>
      );
    }
  }
}

function mapStateToProps(state) {
  return { db: state.forensic.db };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { detectImage, saveToken, saveResults },
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(buttonStyles)(Upload));



// WEBPACK FOOTER //
// ./src/conference/component/Upload.js